import { create } from 'zustand'

import type { Profession } from '../../users/schemas/profession'

interface ConsumerLoginFormData {
  email?: string
  code?: string
}

interface ConsumerSignupFormData {
  firstName?: string
  lastName?: string
  phone?: string
  email?: string
  code?: string
}

interface ProfessionalLoginFormData {
  email?: string
  password?: string
}

interface ProfessionalSignupFormData {
  email?: string
  password?: string
  confirmPassword?: string
}

interface ProfessionalOnboardingFormData {
  firstName?: string
  lastName?: string
  username?: string
  profession?: Profession
}

interface AuthDialogState {
  consumerLoginFormData: ConsumerLoginFormData
  consumerSignupFormData: ConsumerSignupFormData
  professionalLoginFormData: ProfessionalLoginFormData
  professionalSignupFormData: ProfessionalSignupFormData
  professionalOnboardingFormData: ProfessionalOnboardingFormData
}

const ConsumerLoginSteps = ['email', 'otp']
const ConsumerSignupSteps = ['signup', 'verify']
const ProfessionalLoginSteps = ['login']
const ProfessionalSignupSteps = ['signup', 'verify']
const ProfessionalOnboardingSteps = ['firstName', 'lastName', 'username', 'profession']

export const StepNames = {
  ['consumer-login']: ConsumerLoginSteps,
  ['consumer-signup']: ConsumerSignupSteps,
  ['professional-login']: ProfessionalLoginSteps,
  ['professional-signup']: ProfessionalSignupSteps,
  ['professional-onboarding']: ProfessionalOnboardingSteps,
}

export type AuthDialogViews =
  | 'consumer-login'
  | 'consumer-signup'
  | 'consumer-login-success'
  | 'consumer-signup-success'
  | 'professional-login'
  | 'professional-signup'
  | 'professional-onboarding'
  | 'professional-login-success'
  | 'professional-signup-success'
  | 'reset-password'

interface AuthDialogStore {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  view: AuthDialogViews
  setView: (initialView: AuthDialogViews) => void
  step: number
  setStep: (step: number) => void
  authDialogState: AuthDialogState
  setAuthDialogState: (authDialogState: AuthDialogState) => void
  reset: () => void
  getCurrentStepName: () => string
  getCurrentView: () => AuthDialogViews
}

export const useAuthDialogStore = create<AuthDialogStore>((set, get) => ({
  isOpen: false,
  view: 'consumer-login',
  step: 0,
  authDialogState: {
    consumerLoginFormData: {},
    consumerSignupFormData: {},
    professionalLoginFormData: {},
    professionalSignupFormData: {},
    professionalOnboardingFormData: {},
  },
  setIsOpen: (isOpen) => set({ isOpen }),
  setView: (view) => set({ view, step: 0 }),
  setStep: (step) => set({ step }),
  setAuthDialogState: (authDialogState) => set({ authDialogState: { ...authDialogState } }),
  getCurrentStepName: () => {
    const { view, step } = get()
    if (!StepNames[view]) {
      return ''
    }
    return StepNames[view][step] || ''
  },
  getCurrentView: () => {
    const { view } = get()
    return view
  },
  reset: () =>
    set({
      isOpen: false,
      view: 'consumer-login',
      step: 0,
      authDialogState: {
        consumerLoginFormData: {},
        consumerSignupFormData: {},
        professionalLoginFormData: {},
        professionalSignupFormData: {},
        professionalOnboardingFormData: {},
      },
    }),
}))
